<div>
  <h2>{{ '::BrandingSettings' | abpLocalization }}</h2>
  <p class="text-muted">
    {{ '::BrandingSettings:Description' | abpLocalization }}
  </p>
</div>
<br />
<hr />

@if (isLoading) {
  <div class="text-center my-5">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">{{ '::BrandingSettings:Loading' | abpLocalization }}</span>
    </div>
  </div>
} @else if (form) {
  <form [formGroup]="form" (ngSubmit)="submitForm()">
    <div class="mt-4 fade-in-top">
      <!-- Logo Section -->
      <div class="form-group mb-4">
        <label class="form-label">
          {{ '::BrandingSettings:Logo' | abpLocalization }}
          <span class="text-danger">*</span>
        </label>

        @if (!logoUploaded) {
          <!-- Empty State -->
          <div class="upload-area" (click)="triggerLogoInput()">
            <i class="fa-solid fa-upload fa-2x text-muted mb-2"></i>
            <p class="mb-0">
              {{ '::BrandingSettings:ClickToUpload' | abpLocalization }}
            </p>
            <small class="text-muted">PNG, SVG, JPG</small>
          </div>
        } @else {
          <!-- Uploaded State -->
          <div class="uploaded-file-preview">
            <div class="d-flex align-items-center">
              <div class="file-icon-container me-3">
                @if (displayLogoUrl) {
                  <img [src]="displayLogoUrl" alt="Logo preview" class="preview-image" />
                }
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center">
                  <i class="fa-solid fa-check-circle text-success me-2"></i>
                  <span class="fw-semibold">{{ displayLogoFileName }}</span>
                </div>
                <small class="text-muted">
                  <i class="fa-solid fa-check me-1"></i>
                  {{ '::BrandingSettings:Uploaded' | abpLocalization }}
                </small>
              </div>
              <div class="ms-3">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-2"
                  (click)="triggerLogoInput()"
                >
                  {{ '::BrandingSettings:Change' | abpLocalization }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  (click)="onLogoRemove()"
                >
                  {{ '::BrandingSettings:Remove' | abpLocalization }}
                </button>
              </div>
            </div>
          </div>
        }

        <input
          type="file"
          id="logoInput"
          class="d-none"
          accept=".png,.svg,.jpg,.jpeg"
          (change)="onLogoFileSelect($event)"
        />

        <small class="form-text text-muted mt-2 d-block">
          {{ '::BrandingSettings:LogoHelpText' | abpLocalization }}
        </small>
      </div>

      <!-- Favicon Section -->
      <div class="form-group mb-4">
        <label class="form-label">
          {{ '::BrandingSettings:Favicon' | abpLocalization }}
          <span class="text-danger">*</span>
        </label>

        @if (!faviconUploaded) {
          <!-- Empty State -->
          <div class="upload-area" (click)="triggerFaviconInput()">
            <i class="fa-solid fa-upload fa-2x text-muted mb-2"></i>
            <p class="mb-0">
              {{ '::BrandingSettings:ClickToUpload' | abpLocalization }}
            </p>
            <small class="text-muted">ICO, PNG</small>
          </div>
        } @else {
          <!-- Uploaded State -->
          <div class="uploaded-file-preview">
            <div class="d-flex align-items-center">
              <div class="file-icon-container me-3">
                @if (displayFaviconUrl) {
                  <img
                    [src]="displayFaviconUrl"
                    alt="Favicon preview"
                    class="preview-image-small"
                  />
                }
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center">
                  <i class="fa-solid fa-check-circle text-success me-2"></i>
                  <span class="fw-semibold">{{ displayFaviconFileName }}</span>
                </div>
                <small class="text-muted">
                  <i class="fa-solid fa-check me-1"></i>
                  {{ '::BrandingSettings:Uploaded' | abpLocalization }}
                </small>
              </div>
              <div class="ms-3">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-2"
                  (click)="triggerFaviconInput()"
                >
                  {{ '::BrandingSettings:Change' | abpLocalization }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  (click)="onFaviconRemove()"
                >
                  {{ '::BrandingSettings:Remove' | abpLocalization }}
                </button>
              </div>
            </div>
          </div>
        }

        <input
          type="file"
          id="faviconInput"
          class="d-none"
          accept=".ico,.png"
          (change)="onFaviconFileSelect($event)"
        />

        <small class="form-text text-muted mt-2 d-block">
          {{ '::BrandingSettings:FaviconHelpText' | abpLocalization }}
        </small>
      </div>

      <!-- Footer Text Section -->
      <div class="form-group mb-4">
        <label class="form-label" for="footerText">
          {{ '::BrandingSettings:FooterText' | abpLocalization }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="footerText"
          class="form-control"
          formControlName="footerText"
          [maxlength]="footerTextMaxLength"
          [class.is-invalid]="form.get('footerText')?.invalid && form.get('footerText')?.touched"
        />
        <div class="d-flex justify-content-between align-items-start mt-1">
          <small class="form-text text-muted">
            {{ '::BrandingSettings:FooterTextHelpText' | abpLocalization }}
          </small>
          <small class="text-muted"> {{ footerTextLength }} / {{ footerTextMaxLength }} </small>
        </div>
      </div>

      <!-- AI Usage Disclaimer Section -->
      <div class="form-group mb-4">
        <label class="form-label" for="aiUsageDisclaimer">
          {{ '::BrandingSettings:AIUsageDisclaimer' | abpLocalization }}
          <span class="text-danger">*</span>
        </label>
        <textarea
          id="aiUsageDisclaimer"
          class="form-control"
          formControlName="aiUsageDisclaimer"
          rows="8"
          [maxlength]="aiDisclaimerMaxLength"
          [class.is-invalid]="
            form.get('aiUsageDisclaimer')?.invalid && form.get('aiUsageDisclaimer')?.touched
          "
        ></textarea>
        <div class="d-flex justify-content-between align-items-start mt-1">
          <small class="form-text text-muted">
            {{ '::BrandingSettings:AIUsageDisclaimerHelpText' | abpLocalization }}
          </small>
          <small class="text-muted"> {{ aiDisclaimerLength }} / {{ aiDisclaimerMaxLength }} </small>
        </div>
      </div>

      <!-- Save Button -->
      <div class="form-group">
        <button type="submit" class="btn btn-primary" [disabled]="!canSave">
          @if (isSaving) {
            <i class="fa-solid fa-spinner fa-spin me-2"></i>
          } @else {
            <i class="fa-solid fa-check me-2"></i>
          }
          {{ 'AbpUi::Save' | abpLocalization }}
        </button>
      </div>
    </div>
  </form>
}
